import { Button } from '@creditornot/cb-button';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { deleteSshKeyFromIntegrationUser } from 'apis/merchant-gateway/pos-integration-service/sftp-integration/api';
import { WoltModal } from 'common/components';

type ModalProps = {
  merchantId: string;
  keyId: string;
  isOpen: boolean;
  onClose: () => void;
  onDelete: () => void;
};

const StyledModal = styled(WoltModal)`
  background: ${cssThemedValue('surfaceMain')};
  width: 100%;
  max-width: 640px;
  color: ${cssThemedValue('textPrimary')};
`;

const CloseButton = styled(Button).attrs({
  variant: 'lightBlue',
  size: 'small',
})``;

const DeleteButton = styled(Button).attrs({
  variant: 'lightRed',
  size: 'small',
})`
  margin-inline-start: 16px;
`;

const StyledFooter = styled(ModalFooter)`
  justify-content: flex-end;
`;

const ModalContent = styled.div`
  display: flex;
  padding: 32px 24px;
`;

const DeleteSshKeyModal = ({ onClose, onDelete, isOpen, keyId, merchantId }: ModalProps) => {
  const mutation = useMutation(
    async () => {
      return await deleteSshKeyFromIntegrationUser(merchantId, keyId);
    },
    {
      onSuccess: () => {
        onDelete();
      },
    },
  );

  const handleDelete = async () => {
    await mutation.mutateAsync();
  };

  return (
    <StyledModal isOpen={isOpen}>
      <ModalHeader onCloseClick={onClose}>
        <FormattedMessage id="merchant-integrations.sftp.delete-ssh.title" />
      </ModalHeader>
      <ModalContent data-testid="delete-ssh-key-modal-content">
        <FormattedMessage id="merchant-integrations.sftp.delete-ssh.confirmation-text" values={{ keyId }} />
      </ModalContent>
      <StyledFooter>
        <CloseButton data-testid="delete-ssh-key-modal-cancel-btn" onClick={onClose} disabled={mutation.isLoading}>
          <FormattedMessage id="state.cancel" />
        </CloseButton>
        <DeleteButton
          data-testid="delete-ssh-key-modal-delete-btn"
          onClick={() => handleDelete()}
          disabled={mutation.isLoading}
        >
          <FormattedMessage id="state.delete" />
        </DeleteButton>
      </StyledFooter>
    </StyledModal>
  );
};

export default DeleteSshKeyModal;
